import {
  chatSnippets,
  shopSettings,
  type ShopSettingKey,
} from "@/server/platform/db/schema/chat-tools";
import type { DbClient } from "@/server/platform/db/client";
import type { DbTransaction } from "@/shared/types";
import { asc, eq } from "drizzle-orm";
import { nowDate } from "@/shared/lib/date-time";
import type { ChatSnippetDTO } from "../contracts";
import {
  DEFAULT_ORDER_SUMMARY_TEMPLATE,
  DEFAULT_SHOP_DISPLAY_NAME,
} from "../default-template";
import type { ShopSettingsDTO } from "../contracts";

const KEY_MAP: Record<ShopSettingKey, keyof ShopSettingsDTO> = {
  shop_display_name: "shopDisplayName",
  shop_phone: "shopPhone",
  bank_name: "bankName",
  bank_account_number: "bankAccountNumber",
  bank_account_name: "bankAccountName",
  order_summary_template: "orderSummaryTemplate",
};

const REVERSE_KEY_MAP = Object.fromEntries(
  Object.entries(KEY_MAP).map(([k, v]) => [v, k]),
) as Record<keyof ShopSettingsDTO, ShopSettingKey>;

export function getDefaultShopSettings(): ShopSettingsDTO {
  return {
    shopDisplayName: DEFAULT_SHOP_DISPLAY_NAME,
    shopPhone: "",
    bankName: "",
    bankAccountNumber: "",
    bankAccountName: "",
    orderSummaryTemplate: DEFAULT_ORDER_SUMMARY_TEMPLATE,
  };
}

export async function getShopSettings(
  client: DbTransaction | DbClient,
): Promise<ShopSettingsDTO> {
  const defaults = getDefaultShopSettings();
  const rows = await client.select().from(shopSettings);
  const merged = { ...defaults };
  for (const row of rows) {
    const key = row.key as ShopSettingKey;
    const field = KEY_MAP[key];
    if (field) {
      merged[field] = row.value;
    }
  }
  return merged;
}

export async function updateShopSettings(
  client: DbTransaction,
  input: Partial<ShopSettingsDTO>,
) {
  const now = nowDate();
  for (const [field, value] of Object.entries(input)) {
    if (value === undefined) continue;
    const key = REVERSE_KEY_MAP[field as keyof ShopSettingsDTO];
    if (!key) continue;
    await client
      .insert(shopSettings)
      .values({ key, value, updatedAt: now })
      .onConflictDoUpdate({
        target: shopSettings.key,
        set: { value, updatedAt: now },
      });
  }
  return getShopSettings(client);
}

export async function listChatSnippets(
  client: DbTransaction | DbClient,
): Promise<ChatSnippetDTO[]> {
  const rows = await client
    .select()
    .from(chatSnippets)
    .orderBy(asc(chatSnippets.sortOrder), asc(chatSnippets.createdAt));
  return rows.map((r) => ({
    id: r.id,
    title: r.title,
    body: r.body,
    sortOrder: r.sortOrder,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  }));
}

export async function getChatSnippetById(
  client: DbTransaction | DbClient,
  id: string,
) {
  const [row] = await client
    .select()
    .from(chatSnippets)
    .where(eq(chatSnippets.id, id));
  if (!row) return null;
  return {
    id: row.id,
    title: row.title,
    body: row.body,
    sortOrder: row.sortOrder,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

export async function insertChatSnippet(
  client: DbTransaction,
  input: { title: string; body: string; sortOrder: number },
) {
  const now = nowDate();
  const [row] = await client
    .insert(chatSnippets)
    .values({
      title: input.title,
      body: input.body,
      sortOrder: input.sortOrder,
      createdAt: now,
      updatedAt: now,
    })
    .returning();
  if (!row) throw new Error("Failed to create snippet");
  return row;
}

export async function updateChatSnippet(
  client: DbTransaction,
  id: string,
  input: { title?: string; body?: string; sortOrder?: number },
) {
  const now = nowDate();
  const [row] = await client
    .update(chatSnippets)
    .set({
      ...(input.title !== undefined ? { title: input.title } : {}),
      ...(input.body !== undefined ? { body: input.body } : {}),
      ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
      updatedAt: now,
    })
    .where(eq(chatSnippets.id, id))
    .returning();
  return row ?? null;
}

export async function deleteChatSnippet(client: DbTransaction, id: string) {
  const [row] = await client
    .delete(chatSnippets)
    .where(eq(chatSnippets.id, id))
    .returning({ id: chatSnippets.id });
  return row ?? null;
}
